import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import { goldGradientButtonSm } from '@/lib/brandGradient'
import { cn } from '@/lib/cn'
import { glassSurface } from '@/lib/glassSurface'

const CloseIcon = () => (
  <svg aria-hidden className="h-5 w-5" viewBox="0 0 24 24" fill="none">
    <path
      d="M18 6L6 18M6 6l12 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
)

export function ExpertComingSoonModal({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  useBodyScrollLock(open)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const modal = (
    <div
      className="modal-backdrop-in fixed inset-0 z-[100] flex min-h-[100dvh] items-center justify-center bg-[#050505]/82 px-4 py-8 backdrop-blur-[14px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="expert-join-title"
    >
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label="Close dialog"
        onClick={onClose}
      />
      <div
        className={cn(
          'modal-pop-in relative z-[1] box-border flex w-[460px] max-w-[min(460px,calc(100vw-2rem))] flex-col items-center overflow-hidden rounded-3xl px-10 pt-7 pb-6 text-center shadow-2xl',
          'shadow-[0_0_0_1px_rgba(255,255,255,0.06),0_24px_64px_-12px_rgba(0,0,0,0.75)]',
          glassSurface,
        )}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 grid h-8 w-8 place-items-center rounded-full text-slate-500 transition hover:bg-black/[0.05] hover:text-slate-700 dark:text-white/70 dark:hover:bg-white/[0.08] dark:hover:text-white"
          aria-label="Close"
        >
          <CloseIcon />
        </button>

        <span className="grid size-12 place-items-center rounded-full bg-gradient-to-br from-[#F6CB86] to-[#E0A263] text-black shadow-[0_6px_22px_-6px_rgba(224,162,99,0.55)]">
          <svg aria-hidden viewBox="0 0 24 24" className="h-6 w-6" fill="none">
            <path
              d="M12 3l1.9 4.7L18.6 9l-4.7 1.9L12 15.6 10.1 10.9 5.4 9l4.7-1.3L12 3z"
              fill="currentColor"
            />
          </svg>
        </span>

        <p className="mt-4 text-xs font-medium tracking-[0.22em] text-[#E0A263] uppercase">
          Coming Soon
        </p>
        <h2
          id="expert-join-title"
          className="mt-3 text-[20px] leading-snug font-medium tracking-tight text-slate-900 dark:text-white"
        >
          Become an Expert is coming soon
        </h2>
        <p className="mx-auto mt-2 max-w-[360px] text-[13.5px] leading-relaxed text-slate-600 dark:text-white/65">
          We are putting the finishing touches on expert validation. Check back
          soon to share your expertise with founders.
        </p>

        <div className="mt-7 flex w-full justify-center">
          <button
            type="button"
            onClick={onClose}
            className={cn(
              'inline-flex h-10 shrink-0 items-center justify-center px-12 text-[13px] font-semibold',
              goldGradientButtonSm,
            )}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  )

  if (typeof document === 'undefined') return modal
  return createPortal(modal, document.body)
}
